import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { getGetGoalQueryKey, useGetGoal, useUpsertGoal } from '@/api/goal/goal';
import { getGetDailySummaryQueryKey } from '@/api/summary/summary';
import {
  UpsertGoalDtoActivityLevel,
  UpsertGoalDtoSex,
  type GoalWarningDto,
  type UpsertGoalDto,
} from '@/api/model';
import { MedicalDisclaimer } from '@/app/layout';
import { Button } from '@/components/ui/button';
import { Card, CardTitle } from '@/components/ui/card';
import { Field, Input, Select } from '@/components/ui/field';
import { kcal, messageOf } from '@/lib/utils';

const ACTIVITY_LABELS: Record<string, string> = {
  sedentary: 'นั่งทำงาน ไม่ค่อยขยับ',
  light: 'ออกกำลังเบา 1–3 วัน/สัปดาห์',
  moderate: 'ปานกลาง 3–5 วัน/สัปดาห์',
  active: 'หนัก 6–7 วัน/สัปดาห์',
  very_active: 'หนักมาก / ใช้แรงงาน',
};

type FormState = {
  startWeightKg: string;
  targetWeightKg: string;
  targetDate: string;
  heightCm: string;
  age: string;
  sex: UpsertGoalDtoSex;
  activityLevel: UpsertGoalDtoActivityLevel;
};

const EMPTY: FormState = {
  startWeightKg: '',
  targetWeightKg: '',
  targetDate: '',
  heightCm: '',
  age: '',
  sex: UpsertGoalDtoSex.male,
  activityLevel: UpsertGoalDtoActivityLevel.moderate,
};

export function Goal() {
  const queryClient = useQueryClient();
  const goal = useGetGoal();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [warning, setWarning] = useState<GoalWarningDto | null>(null);

  // Seed the form from the saved goal once it arrives, without clobbering
  // edits the user has already started making.
  useEffect(() => {
    if (!goal.data) return;
    setForm((current) =>
      current === EMPTY
        ? {
            startWeightKg: String(goal.data.startWeightKg),
            targetWeightKg: String(goal.data.targetWeightKg),
            targetDate: goal.data.targetDate.slice(0, 10),
            heightCm: String(goal.data.heightCm),
            age: String(goal.data.age),
            sex: goal.data.sex as UpsertGoalDtoSex,
            activityLevel: activityLevelOf(goal.data.activityFactor),
          }
        : current,
    );
    setWarning(goal.data.warning ?? null);
  }, [goal.data]);

  const save = useUpsertGoal({
    mutation: {
      onSuccess: async (saved) => {
        setWarning(saved.warning ?? null);
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getGetGoalQueryKey() }),
          queryClient.invalidateQueries({ queryKey: getGetDailySummaryQueryKey() }),
        ]);
      },
    },
  });

  const set = (key: keyof FormState) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    save.mutate({ data: toPayload(form) });
  };

  return (
    <>
      {goal.data && (
        <Card className="space-y-1 text-center">
          <CardTitle>งบแคลอรีต่อวัน</CardTitle>
          <p className="text-4xl font-semibold tabular-nums">{kcal(goal.data.dailyBudgetKcal)}</p>
          <p className="text-xs text-ink-muted">
            TDEE {kcal(goal.data.tdee)} · BMR {kcal(goal.data.bmr)}
          </p>
        </Card>
      )}

      {warning && (
        <Card className="border-warning/40 bg-warning/10">
          <p className="text-sm">{warning.message}</p>
        </Card>
      )}

      <Card>
        <form className="space-y-4" onSubmit={submit}>
          <CardTitle>ตั้งเป้าหมาย</CardTitle>

          <Field label="น้ำหนักตอนนี้ (กก.)">
            {(props) => (
              <Input
                {...props}
                type="number"
                step="0.1"
                required
                value={form.startWeightKg}
                onChange={(event) => set('startWeightKg')(event.target.value)}
              />
            )}
          </Field>

          <Field label="น้ำหนักเป้าหมาย (กก.)">
            {(props) => (
              <Input
                {...props}
                type="number"
                step="0.1"
                required
                value={form.targetWeightKg}
                onChange={(event) => set('targetWeightKg')(event.target.value)}
              />
            )}
          </Field>

          <Field label="วันที่ต้องการถึงเป้า">
            {(props) => (
              <Input
                {...props}
                type="date"
                required
                value={form.targetDate}
                onChange={(event) => set('targetDate')(event.target.value)}
              />
            )}
          </Field>

          <Field label="ส่วนสูง (ซม.)">
            {(props) => (
              <Input
                {...props}
                type="number"
                required
                value={form.heightCm}
                onChange={(event) => set('heightCm')(event.target.value)}
              />
            )}
          </Field>

          <Field label="อายุ">
            {(props) => (
              <Input
                {...props}
                type="number"
                required
                value={form.age}
                onChange={(event) => set('age')(event.target.value)}
              />
            )}
          </Field>

          <Field label="เพศ">
            {(props) => (
              <Select
                {...props}
                value={form.sex}
                onChange={(event) => set('sex')(event.target.value)}
              >
                <option value={UpsertGoalDtoSex.male}>ชาย</option>
                <option value={UpsertGoalDtoSex.female}>หญิง</option>
              </Select>
            )}
          </Field>

          <Field label="ระดับกิจกรรม">
            {(props) => (
              <Select
                {...props}
                value={form.activityLevel}
                onChange={(event) => set('activityLevel')(event.target.value)}
              >
                {Object.values(UpsertGoalDtoActivityLevel).map((level) => (
                  <option key={level} value={level}>
                    {ACTIVITY_LABELS[level] ?? level}
                  </option>
                ))}
              </Select>
            )}
          </Field>

          {save.isError && <p className="text-sm text-danger">{messageOf(save.error)}</p>}

          <Button type="submit" size="lg" disabled={save.isPending}>
            {save.isPending ? 'กำลังบันทึก…' : 'บันทึกเป้าหมาย'}
          </Button>
        </form>
      </Card>

      <MedicalDisclaimer />
    </>
  );
}

function toPayload(form: FormState): UpsertGoalDto {
  return {
    startWeightKg: Number(form.startWeightKg),
    targetWeightKg: Number(form.targetWeightKg),
    targetDate: form.targetDate,
    heightCm: Number(form.heightCm),
    age: Number(form.age),
    sex: form.sex,
    activityLevel: form.activityLevel,
  };
}

/**
 * The API stores the resolved factor, not the level the user picked, so the
 * form recovers the nearest level to re-select it.
 */
function activityLevelOf(factor: number): UpsertGoalDtoActivityLevel {
  const factors: Array<[UpsertGoalDtoActivityLevel, number]> = [
    [UpsertGoalDtoActivityLevel.sedentary, 1.2],
    [UpsertGoalDtoActivityLevel.light, 1.375],
    [UpsertGoalDtoActivityLevel.moderate, 1.55],
    [UpsertGoalDtoActivityLevel.active, 1.725],
    [UpsertGoalDtoActivityLevel.very_active, 1.9],
  ];

  return factors.reduce((best, entry) =>
    Math.abs(entry[1] - factor) < Math.abs(best[1] - factor) ? entry : best,
  )[0];
}
